/**
 * 행동 신호 — AI 답 뒤에 사람이 무엇을 했는가 (2026-09-12, D33 · D44)
 *
 * 평가 버튼은 잘 안 누른다. 대신 행동을 본다.
 *   EDIT   : AI 가 채운 조건을 사람이 고쳐서 조회했다 → 어느 필드인지 (값은 안 보낸다)
 *   MANUAL : AI 답 뒤에 사람이 그냥 직접 조회했다
 *   REASK  : 그 답을 두고 "다시 시도" 를 눌렀다 — 같은 질문으로 답을 다시 만들었다 (2026-10-14)
 *   GOOD / BAD : 평가 버튼
 *
 * 값은 절대 보내지 않는다. 필드 이름만 보낸다.
 * "AI 가 기간 조건을 채우면 60% 가 고친다" 만 나오면 개선에 충분하다.
 *
 * v2(AI Center) 답변에만 기록이 생기므로 v1 에서는 아무 신호도 나가지 않는다.
 * 훅은 두 곳뿐이다 — applySearchParamsAndRun(AI 조회 표시) · fnGetDataSet(사람 조회 감지).
 */
import { apiFetch } from "@shared/api/api";
import { aiSignalFlag } from "@shared/libs/aiSignalFlag";

export type AiSignalType = "EDIT" | "MANUAL" | "GOOD" | "BAD" | "REASK" | "CONT";

type Remembered = {
  idCall: string;
  /** AI 가 채운 조건. 필드 → 문자열 값. 비교용으로만 들고 있다 */
  params: Record<string, string>;
  at: number;
};

/** AI 답 뒤 이 시간 안의 사람 조회만 신호로 본다. 지나면 무관한 조회다 */
const WINDOW_MS = 10 * 60 * 1000;

/** 메뉴별 마지막 v2 답변. 한 답에 한 번만 신호를 낸다 */
const remembered = new Map<string, Remembered>();

export const aiSignal = {
  /** v2 답변에 화면 조회 지시가 있었다. 어느 조건을 채웠는지 기억한다 */
  rememberAiSearch(menuCode: string | undefined, idCall: string | undefined, params: unknown) {
    if (!menuCode || !idCall) return;
    remembered.set(menuCode, { idCall, params: flat(params), at: Date.now() });
  },

  /** v2 답변만 왔다(조회 지시 없음). 그 뒤 사람이 직접 조회하면 MANUAL 이다 */
  rememberAnswer(menuCode: string | undefined, idCall: string | undefined) {
    if (!menuCode || !idCall) return;
    if (remembered.get(menuCode)?.idCall === idCall) return;
    remembered.set(menuCode, { idCall, params: {}, at: Date.now() });
  },

  /** AI 조회 표시. Global.ts 는 순환을 피해 aiSignalFlag 를 직접 쓴다. 여기 것은 같은 것을 가리킨다 */
  begin: aiSignalFlag.begin,
  end: aiSignalFlag.end,

  /**
   * 사람이 조회했다. AI 가 돌린 조회는 여기 들어오지 않는다(begin/end).
   * 기억된 답이 있고 시간 안이면 신호를 보내고 기억을 지운다.
   */
  onManualSearch(menuCode: string | undefined, requestParams: unknown) {
    if (aiSignalFlag.isRunning() || !menuCode) return;
    const r = remembered.get(menuCode);
    if (!r) return;
    remembered.delete(menuCode);
    if (Date.now() - r.at > WINDOW_MS) return;

    const now = flat(pickForm(requestParams));
    const edited: string[] = [];
    for (const [field, aiValue] of Object.entries(r.params)) {
      if ((now[field] ?? "") !== aiValue) edited.push(field);
    }
    const items: { tpSignal: AiSignalType; field?: string }[] = edited.length
      ? edited.map((f) => ({ tpSignal: "EDIT" as const, field: f }))
      : [{ tpSignal: "MANUAL" as const }];
    void send(r.idCall, menuCode, items);
  },

  /** 평가 버튼 */
  rate(idCall: string, menuCode: string | undefined, good: boolean) {
    return send(idCall, menuCode, [{ tpSignal: good ? "GOOD" : "BAD" }]);
  },

  /** "다시 시도" — 이 답은 마음에 들지 않아 같은 질문을 다시 보냈다. 평가 버튼보다 확실한 신호다 */
  reask(idCall: string, menuCode: string | undefined) {
    return send(idCall, menuCode, [{ tpSignal: "REASK" }]);
  },
};

/* ------------------------------------------------------------------ */

async function send(
  idCall: string,
  cdMenu: string | undefined,
  signals: { tpSignal: AiSignalType; field?: string }[]
) {
  try {
    await apiFetch<boolean>("/api/chat/v2/signal", {
      method: "POST",
      body: { idCall, cdMenu, signals } as unknown as BodyInit,
    });
  } catch {
    // 수집용 부가 기능이다. 사용자 흐름을 막지 않는다
  }
}

/** 조회 요청에서 조건 부분만. 화면마다 { params: formdata } 로 싼다 */
function pickForm(requestParams: unknown): unknown {
  if (requestParams && typeof requestParams === "object" && "params" in requestParams) {
    return (requestParams as { params?: unknown }).params;
  }
  return requestParams;
}

/** 값을 문자열로 편다. 비교만 하고 어디에도 보내지 않는다 */
function flat(o: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (!o || typeof o !== "object") return out;
  for (const [k, v] of Object.entries(o as Record<string, unknown>)) {
    if (v === null || v === undefined) continue;
    if (typeof v === "object") continue;
    out[k] = String(v);
  }
  return out;
}
